/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Grid,
  TextField,
  Step,
  StepLabel,
  Stepper,
  Typography,
  InputAdornment
} from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { SnackbarContext } from 'contexts/SnackbarContext'
import { validatePasswordComplexity } from 'utils/formatUtils'
import PasswordFields from 'pages/ActivateAccount/PasswordFields'
import SectionHeader from 'components/SectionHeader'
import FixedWidthContainer from 'components/FixedWidthContainer'
import {
  confirmActivation,
  getEmailByPendingAccountId,
  requestActivation
} from 'api/signUp'
import { LoadingContext } from 'contexts/LoadingContext'

interface FormInputs {
  email: string
  password: string
  confirmPassword: string
  code: string
}

const ActivateAccountPage = () => {
  const { t } = useTranslation()
  const { accountId } = useParams<{ accountId: string }>()
  const { showMessage } = useContext(SnackbarContext)
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState<number>(1)
  const { showLoading, stopLoading } = useContext(LoadingContext)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isResendDisabled, setIsResendDisabled] = useState(false)
  const [resendLabel, setResendLabel] = useState('')
  const [timer, setTimer] = useState(0)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm<FormInputs>({
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      code: ''
    },
    mode: 'onBlur'
  })

  // Fetch the pending account's email for the route parameter.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchEmail = async () => {
      if (!accountId) return
      showLoading()
      const result = await getEmailByPendingAccountId(accountId)
      if (result.success && result.email) {
        setValue('email', result.email)
      } else {
        showMessage(result.error || t('snackbar.account.loadFailed'), 'error')
        navigate('/login')
      }
      stopLoading()
    }

    fetchEmail()
  }, [accountId, setValue])

  // Initialize resend label
  useEffect(() => {
    setResendLabel(t('activateAccount.resend'))
  }, [t])

  // Countdown timer for resend button
  useEffect(() => {
    let countdown: NodeJS.Timeout
    if (timer > 0) {
      countdown = setTimeout(() => {
        setTimer(prev => prev - 1)
      }, 1000)
    } else if (timer === 0 && isResendDisabled) {
      setIsResendDisabled(false)
      setResendLabel(t('activateAccount.resend'))
    }
    return () => clearTimeout(countdown)
  }, [timer, isResendDisabled, t])

  const handleResendCode = async () => {
    setIsResendDisabled(true)
    setTimer(30)

    showLoading()
    const result = await requestActivation(
      accountId as string,
      watch('password')
    )
    stopLoading()

    if (result.success) {
      showMessage(t('activateAccount.codeSent'))
    } else {
      showMessage(result.error, 'error')
    }
  }

  const onSubmit = async (data: FormInputs) => {
    if (currentStep === 1) {
      const complexityError = validatePasswordComplexity(data.password)
      if (complexityError) {
        showMessage(complexityError, 'error')
        return
      }
      if (data.password !== data.confirmPassword) {
        showMessage(t('snackbar.account.passwordMismatch'), 'error')
        return
      }

      showLoading()
      const result = await requestActivation(accountId as string, data.password)
      stopLoading()

      if (result.success) {
        showMessage(result.message)
        setCurrentStep(2)
        setIsResendDisabled(true)
        setTimer(30)
      } else {
        showMessage(result.error, 'error')
      }
    } else {
      showLoading()
      const result = await confirmActivation(accountId as string, data.code)
      stopLoading()

      if (result.success) {
        showMessage(t('snackbar.account.activationSuccess'))
        navigate('/login')
      } else {
        showMessage(result.error, 'error')
      }
    }
  }

  const handleBack = () => {
    setCurrentStep(1)
  }

  return (
    <FixedWidthContainer>
      <SectionHeader title='ACTIVATE ACCOUNT' />
      <Stepper activeStep={currentStep - 1} alternativeLabel>
        <Step key='step1'>
          <StepLabel>Set Password</StepLabel>
        </Step>
        <Step key='step2'>
          <StepLabel>Verification</StepLabel>
        </Step>
      </Stepper>

      <Box sx={{ maxWidth: '60%', margin: '0 auto' }}>
        <Typography variant='h2' align='center' gutterBottom sx={{ mt: 3 }}>
          {currentStep === 1
            ? 'Enter Account Information'
            : 'Enter Verification Code'}
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label='Email'
                value={watch('email')}
                placeholder='Required'
                fullWidth
                margin='dense'
                variant='outlined'
                required
                disabled
                error={!!errors.email}
                helperText={errors.email?.message || ''}
                inputProps={{ maxLength: 50 }}
                size='medium'
              />
            </Grid>

            {currentStep === 1 && (
              <PasswordFields
                register={register}
                errors={errors}
                watch={watch}
                setValue={setValue}
                showPassword={showPassword}
                showConfirmPassword={showConfirmPassword}
                toggleShowPassword={() => setShowPassword(prev => !prev)}
                toggleShowConfirmPassword={() =>
                  setShowConfirmPassword(prev => !prev)
                }
              />
            )}

            {currentStep === 2 && (
              <Grid item xs={12}>
                <TextField
                  label={t('activateAccount.verificationCode')}
                  variant='outlined'
                  fullWidth
                  autoComplete='one-time-code'
                  {...register('code', {
                    required: t('activateAccount.errors.codeRequired')
                  })}
                  onChange={e =>
                    setValue('code', e.target.value.replace(/\s/g, ''))
                  }
                  error={!!errors.code}
                  helperText={errors.code?.message}
                  inputProps={{ maxLength: 10 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <Button
                          onClick={handleResendCode}
                          disabled={isResendDisabled}
                        >
                          {resendLabel}
                        </Button>
                      </InputAdornment>
                    )
                  }}
                />
                {isResendDisabled && (
                  <Typography
                    variant='body2'
                    sx={{
                      mt: 1,
                      color: 'text.secondary',
                      fontSize: '0.875rem'
                    }}
                  >
                    {t('activateAccount.secondsLeft', { seconds: timer })}
                  </Typography>
                )}
              </Grid>
            )}
          </Grid>

          <Box
            display='flex'
            justifyContent={currentStep === 1 ? 'flex-end' : 'space-between'}
            mt={3}
          >
            {currentStep === 1 && (
              <Button
                variant='contained'
                color='primary'
                type='submit'
                sx={{ padding: '5px', width: '10%' }}
              >
                Next
              </Button>
            )}

            {currentStep === 2 && (
              <>
                <Button
                  variant='outlined'
                  color='primary'
                  onClick={handleBack}
                  sx={{ width: '48%', padding: '5px' }}
                >
                  Back
                </Button>
                <Button
                  variant='contained'
                  color='primary'
                  type='submit'
                  sx={{ width: '48%', padding: '5px' }}
                >
                  Submit
                </Button>
              </>
            )}
          </Box>
        </form>
      </Box>
    </FixedWidthContainer>
  )
}

export default ActivateAccountPage
